import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, Tag, CalendarClock, Users } from "lucide-react";
import { api, errMsg, imgUrl, money, timeAgo, fmtDateTime } from "@/lib/api";
import { Loading, ErrorState, Empty, PageTitle, StatusPill } from "@/components/States";
import { btnPrimary, card } from "@/lib/ui";

const Mini = ({ l, right }) => (
  <div className="flex items-center gap-3 py-2.5">
    <Link to={`/listing/${l.id}`} className="h-12 w-12 rounded-lg overflow-hidden bg-slate-100 shrink-0">{l.photos?.[0] && <img src={imgUrl(l.photos[0])} alt="" className="h-full w-full object-cover" />}</Link>
    <Link to={`/listing/${l.id}`} className="flex-1 min-w-0"><p className="truncate text-sm font-semibold">{l.title}</p><p className="text-xs text-slate-500">{money(l.price)}</p></Link>
    {right}
  </div>
);

const Section = ({ icon: Icon, title, count, children, testId }) => (
  <section data-testid={testId} className={card}>
    <h2 className="flex items-center gap-2 font-bold text-slate-900 mb-2"><Icon className="h-4 w-4 text-blue-600" /> {title} <span className="text-xs font-mono text-slate-400">{count}</span></h2>
    {children}
  </section>
);

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("active");
  const load = useCallback(() => {
    api.get("/dashboard/seller").then(({ data }) => setD(data)).catch((e) => setErr(errMsg(e)));
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const reconfirm = async (id) => {
    try {
      await api.post(`/listings/${id}/reconfirm`);
      toast.success("Availability reconfirmed — listing is live again");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (err) return <ErrorState message={err} onRetry={load} />;
  if (!d) return <Loading />;
  return (
    <div>
      <PageTitle sub="Everything that needs your attention as a seller." right={<Link to="/sell" data-testid="dashboard-new-listing" className={btnPrimary + " whitespace-nowrap"}>New listing</Link>}>Selling</PageTitle>
      <div className="grid grid-cols-4 gap-2 mb-6">
        {["active", "reserved", "paused", "sold"].map((k) => (
          <button key={k} data-testid={`dashboard-count-${k}`} onClick={() => setTab(k)} className={`rounded-2xl border p-3 text-left transition-colors ${tab === k ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white"}`}>
            <div className="text-2xl font-extrabold font-mono">{d.counts[k]}</div>
            <div className="text-xs font-semibold capitalize text-slate-600">{k}</div>
          </button>
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        {d.needs_reconfirm.length > 0 && (
          <Section icon={RefreshCw} title="Still available? Reconfirm" count={d.needs_reconfirm.length} testId="dashboard-reconfirm">
            <p className="text-xs text-slate-500 mb-1">Listings pause after 14 days until you confirm they're still available.</p>
            {d.needs_reconfirm.map((l) => <Mini key={l.id} l={l} right={<button data-testid={`reconfirm-btn-${l.id}`} onClick={() => reconfirm(l.id)} className={btnPrimary + " h-9 px-3"}>Still available</button>} />)}
          </Section>
        )}
        <Section icon={Tag} title="Incoming offers" count={d.incoming_offers.length} testId="dashboard-offers">
          {d.incoming_offers.length === 0 ? <p className="text-sm text-slate-500 py-2">No offers waiting on you.</p> : d.incoming_offers.map((o) => (
            <Link key={o.id} to={`/inbox/${o.conversation_id}`} data-testid={`dashboard-offer-${o.id}`} className="flex items-center justify-between py-2.5 text-sm hover:bg-slate-50 rounded-lg px-1">
              <span className="truncate"><b className="font-mono">{money(o.amount)}</b> from {o.buyer?.first_name} · {o.listing_title}</span><span className="text-xs text-slate-400 shrink-0">{timeAgo(o.created_at)}</span>
            </Link>
          ))}
        </Section>
        <Section icon={CalendarClock} title="Upcoming pickups" count={d.pickups.length} testId="dashboard-pickups">
          {d.pickups.length === 0 ? <p className="text-sm text-slate-500 py-2">No pickups scheduled.</p> : d.pickups.map((a) => (
            <Link key={a.id} to={`/inbox/${a.conversation_id}`} data-testid={`dashboard-pickup-${a.id}`} className="flex items-center justify-between gap-2 py-2.5 text-sm hover:bg-slate-50 rounded-lg px-1">
              <span className="truncate">{a.listing_title} · {a.buyer?.first_name}</span>
              <span className="text-xs shrink-0 font-semibold text-blue-700">{a.status === "confirmed" ? fmtDateTime(a.confirmed_terms.start_at) : a.status.replace("_", " ")}</span>
            </Link>
          ))}
        </Section>
        <Section icon={Users} title="Interested-buyer waitlists" count={d.waitlists.length} testId="dashboard-waitlists">
          {d.waitlists.length === 0 ? <p className="text-sm text-slate-500 py-2">No waitlists yet.</p> : d.waitlists.map((w) => (
            <div key={w.listing_id} className="py-2 text-sm"><b>{w.title}</b>: {w.users.map((u) => u?.first_name).join(", ")}</div>
          ))}
        </Section>
      </div>
      <section className={card + " mt-4"} data-testid={`dashboard-list-${tab}`}>
        <h2 className="font-bold capitalize mb-1">{tab} listings</h2>
        {d.listings[tab].length === 0 ? <Empty title={`No ${tab} listings`} /> : d.listings[tab].map((l) => <Mini key={l.id} l={l} right={<StatusPill status={l.status} />} />)}
      </section>
    </div>
  );
}
